import React, { useEffect, useState, useContext } from "react";
import { FirebaseContext } from "../contexts/FirebaseContext";
import TaskTimeRecordItem from "../components/TaskTimeRecordItem";
import { formatTimeToClock } from "../utils/formatTimeToClock";
import { isAiInvoiceEnabled } from "../utils/billingRateSetting";

const TaskRecordsContainer = ({ task }) => {
  const {
    fetchTimeRecords,
    fetchActions,
    setTaskTrackingState,
    setTaskAiTrackingState,
  } = useContext(FirebaseContext);

  const [timeRecords, setTimeRecords] = useState([]);
  // actionId -> actionLabel, so a record tied to an action can show its name
  // beside the time (Android parity — resolved via a map, not a join).
  const [actionLabelById, setActionLabelById] = useState({});
  const [elapsedTime, setElapsedTime] = useState("00:00:00");
  const [aiElapsedTime, setAiElapsedTime] = useState("00:00:00");

  // AI tracking button shows under the human one when AI invoicing is on AND the
  // task has AI tracking enabled — mirrors Android's task-detail two-button row.
  const showAi = isAiInvoiceEnabled() && task.aiTrackingEnabled !== false;

  // Fetch task time records when taskId changes
  useEffect(() => {
    if (task.id) {
      console.log("Fetching time records for taskId:", task.id);
      const unsubscribe = fetchTimeRecords(task.id, setTimeRecords);
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [task.id]);

  useEffect(() => {
    console.log("Fetched time records:", timeRecords);
  }, [timeRecords]);

  // Subscribe to this task's actions to resolve actionId -> label for records.
  useEffect(() => {
    if (!task.id) return;
    const unsubscribe = fetchActions(task.id, (actions) => {
      const map = {};
      (actions || []).forEach((a) => {
        map[String(a.id)] = a.actionLabel || "";
      });
      setActionLabelById(map);
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [task.id, fetchActions]);

  useEffect(() => {
    let interval;
    if (task.taskBeingTracked) {
      interval = setInterval(() => {
        const now = Date.now();
        const diff = now - task.trackStartedTimestamp;
        setElapsedTime(formatTimeToClock(diff));
      }, 1000);
    } else {
      setElapsedTime("00:00:00");
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [task.taskBeingTracked, task.trackStartedTimestamp]);

  useEffect(() => {
    let interval;
    if (task.aiBeingTracked) {
      interval = setInterval(() => {
        const now = Date.now();
        const diff = now - task.aiTrackStartedTimestamp;
        setAiElapsedTime(formatTimeToClock(diff));
      }, 1000);
    } else {
      setAiElapsedTime("00:00:00");
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [task.aiBeingTracked, task.aiTrackStartedTimestamp]);

  const handleToggleTracking = async () => {
    try {
      await setTaskTrackingState(
        task.id,
        task.projectId,
        !task.taskBeingTracked
      );
    } catch (error) {
      console.error("Error toggling tracking state:", error);
    }
  };

  const handleToggleAiTracking = async () => {
    try {
      await setTaskAiTrackingState(
        task.id,
        task.projectId,
        !task.aiBeingTracked
      );
    } catch (error) {
      console.error("Error toggling AI tracking state:", error);
    }
  };

  const trackingVisible = task.taskState !== "task_state_done";

  return (
    <div style={{ paddingBottom: trackingVisible ? "92px" : 0 }}>
      <h2>Task Records</h2>
      {timeRecords.length > 0 ? (
        <ul style={styles.list}>
          {timeRecords.map((record, index) => (
            <li key={index} style={styles.listItem}>
              <TaskTimeRecordItem
                record={record}
                actionLabel={
                  record.actionId != null
                    ? actionLabelById[String(record.actionId)] || ""
                    : ""
                }
                isAi={record.trackerKind === "ai" && isAiInvoiceEnabled()}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p>No time records found for this task.</p>
      )}

      {trackingVisible && (
        <div style={styles.footer}>
          <div style={styles.trackRow}>
            <button
              onClick={handleToggleTracking}
              style={{ ...styles.trackBtn, backgroundColor: "var(--track-human, var(--primary))" }}
            >
              {task.taskBeingTracked ? `Stop · ${elapsedTime}` : "Start Tracking"}
            </button>
            {showAi && (
              <button
                onClick={handleToggleAiTracking}
                style={{ ...styles.trackBtn, backgroundColor: "var(--track-ai, #3b82f6)" }}
              >
                {task.aiBeingTracked ? `Stop AI · ${aiElapsedTime}` : "Start AI Tracking"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  // Pinned above the bottom bar so only the records list scrolls.
  footer: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: "60px", // height of the fixed bottom nav bar
    padding: "12px 16px",
    background: "var(--bg)",
    borderTop: "1px solid var(--border)",
    boxSizing: "border-box",
    zIndex: 900,
  },
  trackRow: {
    display: "flex",
    gap: "12px",
  },
  trackBtn: {
    flex: 1,
    color: "#fff",
    border: "none",
    padding: "12px 16px",
    borderRadius: "8px",
    cursor: "pointer",
  },
  list: {
    listStyleType: "none",
    padding: 0,
    margin: 0,
  },
  listItem: {
    marginBottom: "8px",
  },
};

export default TaskRecordsContainer;
